import { DestroyRef, inject } from "@angular/core";
import { asyncScheduler, fromEvent, merge, throttleTime } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EmotionEventsHelperService } from "@services/helpers/emotion-events-helper.service";
import { environment } from "@env/environment";

/**
 * Function that creates activity timeout listeners.
 * Listens for user interactions and resets the logout timer accordingly.
 * Must be used within an Angular service/component constructor to ensure proper lifecycle management.
 */
export function createActivityTimeoutListeners(
	duration: number = environment.activityDelay,
	destroyRef: DestroyRef = inject(DestroyRef),
) {
	merge(
		// Preferred over click to capture all mouse interactions (such as drag or right-click)
		fromEvent(document, "mousedown"),
		// Preferred over focus to capture focus events on child elements as well
		fromEvent(document, "focusin"),
		// Preferred over key press to capture all key interactions (including non-character keys)
		fromEvent(document, "keydown"),
		// Preferred over mouse over to capture all mouse movements (such as dragging)
		fromEvent(document, "mousemove"),
		// Use capture option to ensure deep scroll events are captured from document element
		fromEvent(document, "scroll", { capture: true }),
	)
		.pipe(
			takeUntilDestroyed(destroyRef),
			/**
			 * Throttle the events to prevent excessive calls
			 * leading: true - emit the first event immediately
			 * trailing: true - emit the last event after the duration
			 */
			throttleTime(duration, asyncScheduler, { leading: true, trailing: true }),
		)
		.subscribe(() => EmotionEventsHelperService.resetLogoutTimer({ keepAlive: true }));
}
